import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useLazyQuery, gql } from "@apollo/client";
import { ADD_STANDARD_DECISION_TO_IDEA, REMOVE_STANDARD_DECISION_FROM_IDEA } from '../../gql/mutations/standardDecisions';
import { GET_STANDARD_DECISIONS, GET_EVALUATION_TEST_SET_FOR_SELECTED_OPTIONS } from "../../gql/queries/standardDecisions";
import { ACCELIDEA_MID_GREEN, ACCELIDEA_MID_GRAY, ACCELIDEA_DARK_GRAY, ACCELIDEA_LIGHT_GREEN } from '../../styles/colors';
import { Link } from 'react-router-dom';

const DecisionTree2 = ({ 
    standardId,
    decisionData,
    currentDecision,
    setCurrentDecision,
    setDecisionData,
    setActiveRightTab
  }) => {

    // console.log("DecisionTree2 received decisionData:", decisionData);

    const [isCautionDlgOpen, setIsCautionDlgOpen] = useState(false);
    const dialogRef = useRef(null);
    const [isDialogConfirmed, setIsDialogConfirmed] = useState(false);

    const openDialog = () => {
        if(isDialogConfirmed) return; // Already confirmed, no need to show again
        setIsCautionDlgOpen(true);
        dialogRef.current.showModal(); // Use showModal() for a true modal experience
    };

    const closeDialog = () => {
        setIsDialogConfirmed(true);
        setIsCautionDlgOpen(false);
        dialogRef.current.close();
    };


    const [currentData, setCurrentData] = useState(decisionData[currentDecision]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showEndMessage, setShowEndMessage] = useState(false);
    const [evaluationTests, setEvaluationTests] = useState([]);

    const [getStandardEvaluationTests] = useLazyQuery(GET_EVALUATION_TEST_SET_FOR_SELECTED_OPTIONS, {
        fetchPolicy: "no-cache"
    });
    const [getStandardDecisions] = useLazyQuery(GET_STANDARD_DECISIONS, {
		fetchPolicy: "no-cache",
		variables: { standardId: "iso10993", ideaId: "cmg2ovtdf0000ajdk9bnqn652", userId: "user_32vISRGYnuVrL6kntJtzS2UoYJn" },
	});

    // Pull standard decisions data into memory
    useEffect(() => {
        // console.log("**** Current standardId prop in DecisionTree2:", standardId);
        
        getStandardDecisions().then((res) => {
            // console.log("Fetched Standard Decisions:", res);
            // Transform the data into a more accessible format
            const mappedObject = {};
            res.data.getStandardDecisions.forEach((decision, index) => {
            mappedObject[index + 1] = {
                id: decision.id,
                step: decision.step,
                question: decision.question,
                help: decision.help,
                options: decision.options,
                totalDecisions: res.data.getStandardDecisions.length,
                currentDecision: 1,
            };
            });

            // console.log("Mapped Standard Decisions:", mappedObject);
            setDecisionData(mappedObject);
            setCurrentData(mappedObject[currentDecision]);

            setShowEndMessage(false);
        });
        
    }, [getStandardDecisions, currentDecision, selectedOption]);
    
    
    const handleOptionClick = (option) => {
        // console.log("Option clicked:", option);

        
        // Change a selection
        if( selectedOption && selectedOption.option !== option.option ) {
            // console.log("Changing selection to option:", option);

            openDialog();

            // // Show an application popup message that says "Changing your selection will reset subsequent decisions. Are you sure?"
            // if(window.confirm("Changing your selection will impact documentation and reset subsequent decisions. Are you sure?")) {
            //     setSelectedOption(option);
            //     setActiveRightTab("DecisionLog");
            // }
            // else {
            //     // Cancelling change
            //     return;
            // }
        }

        // Remove a selection
        if( selectedOption && selectedOption.option === option.option ) {
            // console.log("Deselecting option:", option);

            // Deselect if clicking the same option
            setSelectedOption(null);

            removeStandardDecisionFromIdea({
                variables: {
                    ideaId: "cmg2ovtdf0000ajdk9bnqn652",
                    standardsDecisionsToMakeId: currentData.id,
                    userId: "user_32vISRGYnuVrL6kntJtzS2UoYJn",
                    standardsDecisionsToMakeOptionsId: option.id
                },
            });
        }

        // New selection
        else {
            // console.log("Selecting option:", option);
            setSelectedOption(option);
            setActiveRightTab("DecisionLog");

            // setDecisionData(prevData => ({
            //     ...prevData,
            //     [currentDecision]: {
            //         ...prevData[currentDecision],
            //         selectedOption: option
            //     }
            // }));


            addStandardDecisionToIdea({
                variables: {
                    ideaId: "cmg2ovtdf0000ajdk9bnqn652",
                    standardsDecisionsToMakeId: currentData.id,
                    userId: "user_32vISRGYnuVrL6kntJtzS2UoYJn",
                    standardsDecisionsToMakeOptionsId: option.id
                },
            });
        }
    };

    const [addStandardDecisionToIdea] = useMutation(ADD_STANDARD_DECISION_TO_IDEA);
    const [removeStandardDecisionFromIdea] = useMutation(REMOVE_STANDARD_DECISION_FROM_IDEA);

    // Function to get currentDecision index from decision id field
    const getDecisionNumFromId = (standardsDecisionsToMakeId) => {
        const decisionArray = Object.values(decisionData);
        for (let i = 0; i < decisionArray.length; i++) {
            if (decisionArray[i].id === standardsDecisionsToMakeId) {
                return i + 1;
            }
        }
        return null; // Not found
    };

    const getOptionStyle = (option) => ({
        display: 'inline-block',
        border: '2px solid black',
        borderRadius: '10px',
        minWidth: '100px',
        padding: '0 20px',
        height: '50px',
        lineHeight: '50px',
        margin: '0 20px',
        cursor: 'pointer',
        backgroundColor: selectedOption && selectedOption.option === option.option ? '#4caf50' : '#fff',
        color: selectedOption && selectedOption.option === option.option ? '#fff' : '#000',
        transition: 'background 0.2s, color 0.2s',
        boxShadow: selectedOption && selectedOption.option === option.option ? '0 0 10px #4caf50' : 'none',
    });

    // Set the next decision so that this view refreshes to show the next decision question and options
    const setNextDecision = () => {
        // console.log("Setting next decision to:", selectedOption.nextDecision);
        const nextDecisionIndex = getDecisionNumFromId(selectedOption.nextDecision)
        setCurrentDecision(nextDecisionIndex);
        const nextDecisionData = decisionData[nextDecisionIndex];
        setCurrentData(nextDecisionData);
    }

    //MG This block has the side effect of activating the Next button when a decision has been made.
    //TODO: Roll whatever is causing that into the useEffect above and get rid of this block.
    useEffect(() => {
        // Ensure selectedOption is set if option.chosen is true (for review mode)
        if (currentData && currentData.options) {
            const chosen = currentData.options.find(opt => opt.chosen);
            if (chosen && (!selectedOption || selectedOption.option !== chosen.option)) {
                // console.log("useEffect: Setting selectedOption from chosen option:", chosen);
                setSelectedOption(chosen);
            }
        }
    }, [currentData, selectedOption]);

    return (
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <div style={{ fontSize: '30px', marginBottom: '60px' }}>
                {currentData ? currentData.question : "Loading..."}
            </div>

            {/* A diamond with a question mark inside, centered above the options */}
            {/* <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }}>
                <div
                    style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: ACCELIDEA_MID_GREEN,
                        transform: 'rotate(45deg)',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                >
                    <span
                        style={{
                            position: 'absolute',
                            top: '6%',
                            left: '46%',
                            transform: 'translate(-50%, 0) rotate(-45deg)',
                            fontSize: '50px',
                            color: '#fff',
                            height: '75%',
                            lineHeight: '1',
                            fontWeight: 'bold',
                            userSelect: 'none',
                        }}
                    >
                        ?
                    </span>
                </div>
            </div> */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                {currentData && currentData.options ? (
                    currentData.options.map((option, index) => {
                        // Determine if this option should be shown as selected
                        const isSelected = selectedOption
                            ? selectedOption.option === option.option
                            : !!option.chosen;
                            // console.log("Rendering option:", option, "isSelected:", isSelected);
                            // console.log("Selected option:", selectedOption, "option.chosen:", option.chosen);
                        return (
                            <div
                                key={index}
                                style={{
                                    ...getOptionStyle(option),
                                    backgroundColor: isSelected ? ACCELIDEA_MID_GREEN : '#fff',
                                    color: isSelected ? '#fff' : '#000',
                                    minHeight: '60px',
                                    minWidth: '120px',
                                    lineHeight: 'normal',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    whiteSpace: 'pre-wrap',
                                    textAlign: 'center',
                                    height: 'auto',
                                    wordBreak: 'break-word',
                                }}
                                onClick={() => handleOptionClick(option)}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = isSelected ? ACCELIDEA_MID_GREEN : '#e0e0e0'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = isSelected ? ACCELIDEA_MID_GREEN : '#fff'}
                            >
                                <span style={{ fontSize: '16px', width: '100%' }}>{option.option}</span>
                            </div>
                        );
                    })
                ) : (
                    <div>Loading options...</div>
                )}
            </div>
    
            <div style={{ marginTop: '30px', marginBotton: '30px', fontSize: '16px' }}>
                <Link onClick={() => setActiveRightTab("HelpDecide")}>
                    Help me decide
                </Link>
            </div>

            <div style={{ marginTop: '80px', display: 'flex', justifyContent: 'center', gap: '24px' }}>
                {/* <button
                    disabled={currentDecision <= 0}
                    style={{
                        padding: '12px 32px',
                        fontSize: '18px',
                        borderRadius: '8px',
                        backgroundColor: currentDecision > 0 ? ACCELIDEA_DARK_GRAY : ACCELIDEA_MID_GRAY,
                        color: '#fff',
                        border: 'none',
                        cursor: currentDecision > 0 ? 'pointer' : 'not-allowed',
                        boxShadow: currentDecision > 0 ? `0 2px 8px ${ACCELIDEA_DARK_GRAY}` : 'none',
                        transition: 'background 0.2s',
                    }}
                    onClick={() => {
                        if (currentDecision > 1) {
                            setCurrentDecision(currentDecision - 1);
                            setCurrentData(decisionData[currentDecision - 1]);
                            setSelectedOption(null);
                        } else if (currentDecision === 1) {
                            setSelectedTree(null);
                        }
                    }}
                >
                    Back
                </button> */}

                <dialog ref={dialogRef} onClose={closeDialog}>
                    <div style={{ marginTop: '20px', fontSize: '26px', color: ACCELIDEA_DARK_GRAY }}>
                        <div style={{ border: `2px solid ${ACCELIDEA_LIGHT_GREEN}`, padding: '20px', borderRadius: '8px' }}>
                            <h2>Warning</h2>
                            <p>Changing your selection will impact documentation and reset subsequent decisions.</p>
                            <p>Are you sure?</p>
                        </div>
                    </div>
                    <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center', gap: '24px' }}>
                        <button 
                            onClick={closeDialog}
                            style={{ fontSize: '22px', padding: '12px 32px' }}
                        >
                            Yes
                        </button>
                        <button 
                            onClick={closeDialog}
                            style={{ fontSize: '22px', padding: '12px 32px' }}
                        >
                            No
                        </button>
                    </div>
                </dialog>
                                
                <button
                    disabled={!selectedOption}
                    style={{
                        padding: '12px 32px',
                        fontSize: '18px',
                        borderRadius: '8px',
                        backgroundColor: selectedOption ? ACCELIDEA_DARK_GRAY : ACCELIDEA_MID_GRAY,
                        color: '#fff',
                        border: 'none',
                        cursor: selectedOption ? 'pointer' : 'not-allowed',
                        boxShadow: selectedOption ? `0 2px 8px ${ACCELIDEA_LIGHT_GREEN}` : 'none',
                        transition: 'background 0.2s',
                    }}
                    onClick={async () => {
                        if (selectedOption) {
                            if(selectedOption.nextDecision === null) {
                                // console.log("End of decision tree reached.");

                                // Show a box below the buttons that says "You have reached the end of this decision tree."
                                // Also show a button that says "Review decisions" that switches to the Decision Log view.
                                setActiveRightTab("DecisionLog");
                                setShowEndMessage(true);

                                // Concatenate the chosen options ids from all decisions and use that to fetch the standard evaluation tests
                                const selectedOptionIds = Object.values(decisionData)
                                    .map(decision => {
                                        const chosenOption = decision.options.find(opt => opt.chosen);
                                        return chosenOption ? chosenOption.id : null;
                                    })
                                    .filter(id => id !== null);
                                // console.log("Selected option IDs for all decisions:", selectedOptionIds);

                                // Concatenate last two selected options
                                const lastTwoSelectedOptions = selectedOptionIds.slice(-2).join(',');
                                //console.log("Last two selected option IDs:", lastTwoSelectedOptions);
                                    
                                await getStandardEvaluationTests({ variables: { optionIds: lastTwoSelectedOptions } }).then((res) => {
                                    // console.log("Fetched Evaluation Tests:", res);
                                    setEvaluationTests(res);
                                });
                            }

                            const selectedObj = currentData.options.find(opt => opt.option === selectedOption.option);
                            if (selectedObj && selectedObj.nextDecision) {
                                setNextDecision();
                                setSelectedOption(null);
                                setActiveRightTab("DecisionLog");
                            }
                        }
                    }}
                >
                    Next
                </button>
            </div>
            <div>
                {showEndMessage && (
                    <div style={{ marginTop: '20px', fontSize: '26px', color: ACCELIDEA_DARK_GRAY }}>
                        <div style={{ border: `2px solid ${ACCELIDEA_LIGHT_GREEN}`, padding: '20px', borderRadius: '8px' }}>
                            <strong>Good Work! You have completed</strong>
                            <br />
                            <strong>ISO 10993-1</strong>
                            <br />
                            <div style={{ textAlign: 'left', marginLeft: '20px',marginTop: '20px', fontSize: '18px', color: ACCELIDEA_DARK_GRAY }}>
                                    {/* Show different text for each of the last 3 options values */}
                                    {selectedOption && selectedOption.id === "opt1_2" && (
                                        <div>
                                            <strong>5.2.1 Non-contact devices</strong>
                                            <br />
                                            <strong>Biological evaluation not applicable</strong>
                                            <ul>
                                                <li>Add documentation supporting non-contact to your submission</li>
                                            </ul>
                                        </div>
                                    )}
                                    {selectedOption && selectedOption.id !== "opt1_2" && evaluationTests && evaluationTests.data && (
                                        <div>
                                            <strong>Automatic updates by Accelidea:</strong>
                                            <ul>
                                                {/* Loop through the evaluationTests array and show each test name as a list item */}
                                                {evaluationTests.data.getEvaluationTests.map((test, index) => (
                                                    <li key={index}>{test.name} was added to the Standards list</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DecisionTree2;